'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Bot, FileCheck, Calendar, Activity } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Stacked card – absolutely positioned inside the single sticky viewport
// ─────────────────────────────────────────────────────────────────────────────

function StackedCard({
  feature,
  index,
  totalCards,
  progress,
}: {
  feature: {
    id: number;
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    accent: string;
    mockup: React.ReactNode;
  };
  index: number;
  totalCards: number;
  progress: ReturnType<typeof useScroll>['scrollYProgress'];
}) {
  const isLast = index === totalCards - 1;
  const step = 1 / totalCards;

  // Each card owns one "step" of scroll progress.
  // It fades out in the last 40 % of its step so the exit is complete before
  // the next card's step begins – no overlapping semi-transparent cards.
  const p0 = index * step;
  const fadeStart = p0 + step * 0.6;
  const fadeEnd = p0 + step;

  const scale = useTransform(
    progress,
    isLast ? [0, 1] : [p0, fadeEnd],
    isLast ? [1, 1] : [1, 0.96],
  );
  const opacity = useTransform(
    progress,
    isLast ? [0, 1] : [p0, fadeStart, fadeEnd],
    isLast ? [1, 1] : [1, 1, 0],
  );

  // Reverse z-index: card 0 on top (visible first), card N-1 on bottom.
  // Because all cards are absolute siblings inside the sticky viewport they
  // are always in frame – no sticky-timing gap between transitions.
  const zIndex = totalCards - index;

  const Icon = feature.icon;

  return (
    <motion.div
      style={{ scale, opacity, zIndex }}
      className="absolute inset-0 rounded-2xl overflow-hidden transform-gpu origin-top will-change-transform border border-white/10"
    >
      {/* Dark base + per-card subtle tint */}
      <div
        className={`absolute inset-0 bg-[#0c0c0c] bg-gradient-to-br ${feature.accent} to-transparent`}
      />

      <div className="relative z-10 w-full h-full flex flex-col md:flex-row">
        {/* Left panel – vertically centred so content is visible on all viewports */}
        <div className="w-full md:w-[40%] h-full px-14 py-12 flex flex-col justify-center">
          <div className="mb-6 w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <h3
            className="text-[36px] font-serif text-white mb-4"
            style={{ lineHeight: '48px', fontWeight: 400 }}
          >
            {feature.title}
          </h3>
          <p className="text-white/60 text-[16px] leading-relaxed max-w-[400px]">
            {feature.description}
          </p>
        </div>

        {/* Right panel – dark mockup */}
        <div className="w-full md:w-[60%] h-full pt-10">
          <div className="w-full h-full rounded-tl-2xl bg-white/[0.02] border-l border-t border-white/10 relative overflow-hidden">
            <div className="relative z-20 w-full h-full">
              {feature.mockup ?? (
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/30 font-medium text-sm">
                  Feature Preview
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section
// ─────────────────────────────────────────────────────────────────────────────

export function StackedFeatures() {
  const t = useTranslations('landing.features');

  const features = [
    {
      id: 1,
      title: t('f1Title'),
      description: t('f1Desc'),
      icon: Bot,
      accent: 'from-blue-500/[0.06] via-transparent',
      mockup: (
        <div className="absolute inset-0 flex flex-col p-6 gap-3">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-white/5 border border-white/10 flex items-center justify-center">
                <div className="w-3 h-3 rounded-sm bg-primary/60" />
              </div>
              <div className="h-2.5 w-32 bg-white/10 rounded-full" />
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-emerald-400/80" />
              <span className="text-[9px] text-emerald-300/70 font-medium">Analyzed</span>
            </div>
          </div>
          {/* Body */}
          <div className="flex-1 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3 flex flex-col gap-2">
              <span className="text-[9px] text-white/40 font-semibold uppercase tracking-wider">
                Extracted Competencies
              </span>
              {[
                'System Design & Architecture',
                'TypeScript / Node.js',
                'Team Leadership',
                'CI/CD & DevOps',
                'Communication Skills',
              ].map((skill, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary/60" />
                  <span className="text-[11px] text-white/60">{skill}</span>
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3 flex flex-col justify-between">
              <div className="space-y-2">
                {[
                  'System Design & Architecture',
                  'TypeScript / Node.js',
                  'Team Leadership',
                  'CI/CD & DevOps',
                  'Communication Skills',
                ].map((_skill, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-[10px] text-white/50">
                      {['System Design', 'TypeScript', 'Leadership', 'CI/CD', 'Communication'][i]}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-1 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary/50 rounded-full"
                          style={{ width: [90, 85, 75, 80, 65][i] + '%' }}
                        />
                      </div>
                      <span className="text-[9px] text-white/30 font-mono">
                        {[90, 85, 75, 80, 65][i]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-white/5">
                <span className="text-[9px] text-white/35">
                  12 suggested interview questions generated
                </span>
              </div>
            </div>
          </div>
          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="h-2 w-20 bg-white/5 rounded-full" />
            <span className="text-[10px] text-primary/60">Review →</span>
          </div>
        </div>
      ),
    },
    {
      id: 2,
      title: t('f2Title'),
      description: t('f2Desc'),
      icon: FileCheck,
      accent: 'from-purple-500/[0.06] via-transparent',
      mockup: (
        <div className="absolute inset-0 flex flex-col p-6 gap-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-white/50 font-medium">Interview Questionnaire</span>
            <span className="text-[9px] text-white/30">Senior Software Engineer · 8 questions</span>
          </div>
          <div className="flex-1 space-y-2 overflow-auto">
            {[
              'Describe your experience designing distributed systems at scale.',
              'Walk us through a recent architectural decision and the trade-offs.',
              'How do you approach mentoring junior engineers on your team?',
              'Tell me about a time you had to push back on a product requirement.',
              'What is your approach to ensuring code quality in a fast-paced team?',
            ].map((q, i) => (
              <div
                key={i}
                className="flex items-start gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/5"
              >
                <span className="text-[9px] text-white/20 font-mono mt-0.5">{i + 1}.</span>
                <div className="flex-1">
                  <p className="text-[11px] text-white/65 leading-relaxed">{q}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-white/5 text-white/30">
                      {['Technical', 'System Design', 'Leadership', 'Behavioral', 'Process'][i]}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 mt-0.5">
                  <div className="w-3 h-3 rounded text-white/10 flex items-center justify-center">
                    ↕
                  </div>
                  <div className="w-3 h-3 rounded text-white/10 flex items-center justify-center">
                    ✕
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button className="text-[10px] text-primary/60 border border-primary/20 rounded-full px-3 py-1">
              + Add
            </button>
            <button className="text-[10px] text-white/40 ml-auto">Save Draft</button>
          </div>
        </div>
      ),
    },
    {
      id: 3,
      title: t('f3Title'),
      description: t('f3Desc'),
      icon: Calendar,
      accent: 'from-emerald-500/[0.06] via-transparent',
      mockup: (
        <div className="absolute inset-0 flex flex-col p-6 gap-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-white/50 font-medium">Deploy Screening</span>
            <span className="text-[9px] text-white/30">Frontend Engineer · Batch #12</span>
          </div>
          <div className="flex-1 flex flex-col gap-3">
            <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
              <span className="text-[9px] text-white/35 font-semibold uppercase tracking-wider">
                Invite Candidates
              </span>
              <div className="mt-2 p-2 rounded-lg bg-white/[0.02] border border-white/5">
                <span className="text-[10px] text-white/25">
                  Enter email addresses or upload CSV...
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {['sarah@acme.co', 'marcus.t@gmail.com', 'priya.m@outlook.com', '+4 more'].map(
                  (email, i) => (
                    <span
                      key={i}
                      className="text-[9px] px-2 py-0.5 rounded-full bg-white/5 text-white/40 border border-white/5"
                    >
                      {email}
                    </span>
                  ),
                )}
              </div>
              <button className="mt-2 text-[9px] text-primary/50">Upload CSV</button>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
              <span className="text-[9px] text-white/35 font-semibold uppercase tracking-wider">
                Interview Settings
              </span>
              <div className="mt-2 space-y-2">
                {[
                  ['Deadline', 'May 18, 2026'],
                  ['Interview Duration', '25 minutes'],
                  ['Time Zone', 'Candidate local time'],
                  ['Reminders', '24h & 1h before deadline'],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-[10px] text-white/40">{label}</span>
                    <span className="text-[10px] text-white/60">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button className="w-full py-2 rounded-full bg-primary/10 border border-primary/20 text-[11px] text-primary font-medium">
            Send Invitations
          </button>
        </div>
      ),
    },
    {
      id: 4,
      title: t('f4Title'),
      description: t('f4Desc'),
      icon: Activity,
      accent: 'from-amber-500/[0.06] via-transparent',
      mockup: (
        <div className="absolute inset-0 flex flex-col p-6 gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400/30 to-purple-400/30 flex items-center justify-center text-[10px] text-white font-bold">
              SK
            </div>
            <div>
              <span className="text-[11px] text-white/70 font-medium">Sarah Kim</span>
              <span className="text-[9px] text-white/30 ml-2">Senior Software Engineer</span>
            </div>
            <span className="ml-auto text-[10px] text-white/25">Screened May 14</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full border-4 border-emerald-400/50 flex items-center justify-center">
              <span className="text-lg font-mono text-emerald-400 font-bold">91</span>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-1">
              {[
                'System Design',
                'Problem Solving',
                'Communication',
                'Leadership',
                'Culture Fit',
              ].map((label, i) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-[9px] text-white/40">{label}</span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-10 h-1 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-400/60 rounded-full"
                        style={{ width: [88, 91, 89, 85, 87][i] + '%' }}
                      />
                    </div>
                    <span className="text-[9px] text-white/40 font-mono">
                      {[88, 91, 89, 85, 87][i]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
            <span className="text-[9px] text-white/35 font-semibold uppercase tracking-wider">
              AI Summary
            </span>
            <p className="mt-1 text-[10px] text-white/55 leading-relaxed">
              &ldquo;Exceptionally strong candidate with deep systems expertise. Demonstrated clear
              architectural thinking and excellent stakeholder communication. Recommend advancing to
              the final round.&rdquo;
            </p>
            <button className="mt-2 text-[10px] text-primary/60">Advance →</button>
          </div>
        </div>
      ),
    },
  ];

  // containerRef sits on the scroll-driver div (not the section) so that
  // scrollYProgress cleanly tracks 0→1 through the card stack height only.
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const CARD_HEIGHT = 560;
  const stackHeight = features.length * 600; // extra 40 px per card for breathing room

  return (
    <section id="features" className="relative bg-black">
      {/* Header in normal flow – always visible at the top of the section */}
      <div className="py-24 px-4 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">
          {t('badge')}
        </p>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-4 leading-[1.1] tracking-tight">
          {t('headline1')}
          <br />
          <span className="text-white/40">{t('headline2')}</span>
        </h2>
      </div>

      {/* Scroll driver: scrolling through this div advances scrollYProgress 0→1 */}
      <div
        ref={containerRef}
        className="relative px-4 pb-24"
        style={{ height: `${stackHeight}px` }}
      >
        {/* Single sticky viewport – all cards live here as absolute siblings.
            Because they share one sticky frame there is NO per-card sticky gap. */}
        <div
          className="sticky top-24 w-full max-w-[1488px] mx-auto"
          style={{ height: `${CARD_HEIGHT}px` }}
        >
          {features.map((feature, index) => (
            <StackedCard
              key={feature.id}
              feature={feature}
              index={index}
              totalCards={features.length}
              progress={scrollYProgress}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
